import type { RulesetOverride } from '../api/types'
import { yearsOf, writeRoundValues, type Scenario } from './scenario'

/**
 * Translating the URL's scenario into what the API takes.
 *
 * Two forms, because there are two surfaces: query parameters for the
 * standings and progression endpoints, and a body for the simulation
 * endpoint. They are built from the same scenario here so that a sweep and a
 * single season cannot end up answering different questions -- the failure
 * mode the backend's own single normaliser exists to prevent.
 *
 * Every key is omitted when unset, so a request for the season as it happened
 * carries no scenario at all and hits the cache like any other.
 */

/** Query parameters shared by the standings and progression routes. */
export interface ScenarioParams {
  preset?: string
  ruleset?: string
  best_rounds_mode?: 'ALL' | 'ALL_BUT_ONE' | 'BEST_N'
  best_rounds_n?: number
  exclude_rounds?: string
  exclude_drivers?: string
  exclude_constructors?: string
  fastest_lap_rule?: 'NONE' | 'ANY' | 'SHARED' | 'TOP_N'
  fastest_lap_points?: string
  fastest_lap_max_position?: number
  round_multipliers?: string
  ignore_adjustments?: boolean
  bar_champions?: boolean
  per_session?: 'SUM_POINTS' | 'BEST_POINTS'
  eligibility?: 'ALL' | 'HAS_POINT' | 'HAS_FINISH' | 'HAS_FINISH_OR_QUALI'
  tie_break?: 'NONE' | 'COUNTBACK_ALL' | 'COUNTBACK_COUNTED'
  countback_depth?: number
  shared_drive?: 'NONE' | 'NO_POINTS' | 'SHARED' | 'SHARED_MULTI' | 'SHARED_HIGHEST_FINISH'
}

export function toParams(scenario: Scenario): ScenarioParams {
  const params: ScenarioParams = {}

  if (scenario.preset !== null) params.preset = scenario.preset
  if (scenario.schemes.length > 0) params.ruleset = scenario.schemes.join(',')
  if (scenario.drop !== null) {
    params.best_rounds_mode = scenario.drop.mode
    if (scenario.drop.mode === 'BEST_N' && scenario.drop.n !== null) {
      params.best_rounds_n = scenario.drop.n
    }
  }
  if (scenario.excludeRounds.length > 0)
    params.exclude_rounds = scenario.excludeRounds.join(',')
  if (scenario.excludeDrivers.length > 0) {
    params.exclude_drivers = scenario.excludeDrivers.join(',')
  }
  if (scenario.excludeConstructors.length > 0) {
    params.exclude_constructors = scenario.excludeConstructors.join(',')
  }
  if (scenario.fastestLap !== null) {
    params.fastest_lap_rule = scenario.fastestLap.rule
    params.fastest_lap_points = scenario.fastestLap.points
    if (scenario.fastestLap.maxPosition !== null) {
      params.fastest_lap_max_position = scenario.fastestLap.maxPosition
    }
  }
  if (scenario.roundValues !== null)
    params.round_multipliers = writeRoundValues(scenario.roundValues)
  if (scenario.countPenalised) params.ignore_adjustments = true
  if (scenario.barChampions) params.bar_champions = true
  if (scenario.perSession !== null) params.per_session = scenario.perSession
  if (scenario.eligibility !== null) params.eligibility = scenario.eligibility
  if (scenario.tieBreak !== null) {
    params.tie_break = scenario.tieBreak
    if (scenario.countback !== null) params.countback_depth = scenario.countback
  }
  if (scenario.sharedDrive !== null) params.shared_drive = scenario.sharedDrive

  return params
}

/**
 * The simulation body, which takes the same rules under their full names.
 *
 * `point_schemes` rather than `point_scheme` whenever there is more than one,
 * because the API refuses both at once -- the same rule the query form follows.
 */
export function toRulesetOverride(scenario: Scenario): RulesetOverride {
  const override: RulesetOverride = {}

  if (scenario.preset !== null) override.preset = scenario.preset
  if (scenario.schemes.length === 1) override.point_scheme = scenario.schemes[0] ?? null
  else if (scenario.schemes.length > 1) override.point_schemes = [...scenario.schemes]
  if (scenario.drop !== null) {
    override.best_rounds_mode = scenario.drop.mode
    if (scenario.drop.mode === 'BEST_N' && scenario.drop.n !== null) {
      override.best_rounds_n = scenario.drop.n
    }
  }
  if (scenario.excludeRounds.length > 0) override.exclude_rounds = [...scenario.excludeRounds]
  if (scenario.excludeDrivers.length > 0)
    override.exclude_drivers = [...scenario.excludeDrivers]
  if (scenario.excludeConstructors.length > 0) {
    override.exclude_constructors = [...scenario.excludeConstructors]
  }
  if (scenario.fastestLap !== null) {
    override.fastest_lap = {
      rule: scenario.fastestLap.rule,
      points: scenario.fastestLap.points,
      max_position: scenario.fastestLap.maxPosition,
    }
  }
  if (scenario.roundValues !== null) {
    override.round_multipliers = Object.fromEntries(
      Object.entries(scenario.roundValues).map(([round, value]) => [Number(round), value]),
    )
  }
  if (scenario.countPenalised) override.ignore_adjustments = true
  if (scenario.barChampions) override.bar_champions = true
  if (scenario.perSession !== null) override.per_session = scenario.perSession
  if (scenario.eligibility !== null) override.eligibility = scenario.eligibility
  if (scenario.tieBreak !== null) {
    override.tie_break = scenario.tieBreak
    if (scenario.countback !== null) override.countback_depth = scenario.countback
  }
  if (scenario.sharedDrive !== null) override.shared_drive = scenario.sharedDrive

  return override
}

/** The whole simulation request, for one season or a sweep of them. */
export function toSimulationBody(scenario: Scenario, options: { delta?: boolean } = {}) {
  return {
    years: yearsOf(scenario),
    championship: scenario.championship === 'constructors' ? 'CONSTRUCTORS' : 'DRIVERS',
    ruleset: toRulesetOverride(scenario),
    include_official_delta: options.delta ?? true,
  } as const
}
